/**
 * Prérequis DÉRIVÉS d'une autre étape du parcours.
 *
 * Deux étapes ne se déverrouillent pas sur une déclaration du Relais mais sur
 * l'état d'une étape voisine :
 *   • Certificat de résidence ← Attestation de domicile (`attestation`)
 *   • Timbre fiscal CAS-A/H   ← Certificat de perte      (`certificat_perte`)
 *
 * Le serveur ne devine pas ces liens : il valide le `dependency_status` que le
 * client lui envoie (`parcours.controller.js`). La dérivation doit donc être
 * calculée UNE fois et servir à la fois à l'affichage, au déverrouillage du
 * bouton, et au payload envoyé — sinon l'écran et le serveur se contredisent.
 *
 * C'est exactement ce qui est arrivé au timbre fiscal (v4.62 → 31/07/2026) :
 * la dérivation existait pour l'affichage, pas pour le payload. Les 5 documents
 * s'affichaient « Obtenu » et « Passer à l'inscription » répondait
 * « Timbre fiscal: Prérequis non satisfaits », sans issue possible.
 */

/**
 * Titre comparable : minuscules SANS accents, comme `normalizeTitreForOrder` côté
 * serveur (`parcours.service.js`). Un titre « Certificat de residence » en base
 * doit dériver son prérequis comme sa variante accentuée : sinon la dérivation
 * saute silencieusement et on retombe sur le cul-de-sac « Prérequis non satisfaits ».
 */
const norm = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

/** Étape « Attestation de domicile » (dite aussi attestation de résidence). */
export function findAttestationIndex(etapes) {
  return (etapes || []).findIndex((e) => {
    const t = norm(e?.titre);
    return t.includes('attestation de domicile') || t.includes('attestation de residence');
  });
}

/** Étape « Certificat de résidence » — à ne pas confondre avec l'attestation. */
export function findCertificatResidenceIndex(etapes) {
  return (etapes || []).findIndex((e) => {
    const t = norm(e?.titre);
    return t.includes('certificat de residence') && !t.includes('attestation');
  });
}

/** Étape « Certificat de perte » (CAS-A / CAS-H — première démarche police). */
export function findCertificatPerteIndex(etapes) {
  return (etapes || []).findIndex((e) => norm(e?.titre).includes('certificat de perte'));
}

export function isCertificatPerteStepObtained(etapes) {
  const idx = findCertificatPerteIndex(etapes);
  if (idx < 0) return false;
  return ((etapes || [])[idx]?.document_statut || 'needed') === 'obtained';
}

export function isTimbreFiscalStep(etape) {
  return norm(etape?.titre).includes('timbre fiscal');
}

/** Le timbre porte-t-il le prérequis « certificat de perte » (CAS-A/H) ? */
export function hasPertePrereq(etape) {
  return (
    etape?.depends_on?.type === 'prerequisites' &&
    Array.isArray(etape?.depends_on?.items) &&
    etape.depends_on.items.some((it) => it?.key === 'certificat_perte')
  );
}

/**
 * Le prérequis de `etape` qui se déduit d'une étape voisine, s'il existe.
 * Source unique de vérité : quelle clé dérive de quelle étape, et son état.
 *
 * @param {object[]} etapes Liste affichée/éditée (l'état local, pas la base :
 *   c'est ce que le Relais voit qui doit partir au serveur).
 * @param {object} etape Étape membre de `etapes`.
 * @returns {{ key: string, obtained: boolean }|null}
 */
export function siblingPrereq(etapes, etape) {
  if (!etape) return null;
  const list = etapes || [];

  const certIdx = findCertificatResidenceIndex(list);
  if (certIdx >= 0 && list[certIdx]?.id === etape.id) {
    const attIdx = findAttestationIndex(list);
    return {
      key: 'attestation',
      obtained: attIdx >= 0 && (list[attIdx]?.document_statut || 'needed') === 'obtained',
    };
  }

  if (isTimbreFiscalStep(etape) && hasPertePrereq(etape)) {
    return { key: 'certificat_perte', obtained: isCertificatPerteStepObtained(list) };
  }

  return null;
}

/**
 * Étape enrichie pour l'AFFICHAGE et le calcul « prérequis satisfaits » : le
 * prérequis dérivé reflète exactement l'étape voisine, dans les deux sens
 * (repasser le voisin à « À demander » re-verrouille l'étape à l'écran).
 *
 * `autoDerivedItemKeys` indique au panneau de prérequis les clés qu'il doit
 * rendre en lecture seule — le Relais ne les coche pas à la main.
 *
 * @returns {{ etape: object, autoDerivedItemKeys: string[]|null }}
 */
export function withDerivedPrereqs(etapes, etape) {
  const derived = siblingPrereq(etapes, etape);
  if (!derived) return { etape, autoDerivedItemKeys: null };
  return {
    etape: {
      ...etape,
      dependency_status: {
        ...etape.dependency_status,
        items: {
          ...etape.dependency_status?.items,
          [derived.key]: { status: derived.obtained ? 'obtained' : 'needed' },
        },
      },
    },
    autoDerivedItemKeys: [derived.key],
  };
}

/**
 * `dependency_status` à ENVOYER au serveur pour `etape`.
 *
 * Ne fait que MONTER `needed` → `obtained` : redescendre un prérequis déjà
 * enregistré comme obtenu ferait refuser par le serveur un document lui-même
 * déjà obtenu (`Prérequis non satisfaits`) — on remplacerait un cul-de-sac par
 * un autre. Le verrouillage à l'écran, lui, reste bidirectionnel
 * (`withDerivedPrereqs`).
 */
export function mergeDerivedPrereqStatus(etapes, etape, depStatus = etape?.dependency_status) {
  const derived = siblingPrereq(etapes, etape);
  if (!derived) return depStatus;
  return {
    ...depStatus,
    items: {
      ...depStatus?.items,
      [derived.key]: derived.obtained
        ? { status: 'obtained' }
        : depStatus?.items?.[derived.key] || { status: 'needed' },
    },
  };
}
